import { CabinetType, ShelvingType } from "building-types/casework/caseworkTypesEnum";
import type { Doc } from "building-types/doc";
import type { DocEntityType } from "building-types/doc/docEntityTypes";
import { DoorOperationType } from "building-types/doors/doorOperationTypes";
import type { Drawing } from "building-types/drawings/drawingTypes";
import { DrawingId, DrawingType } from "building-types/drawings/drawingTypesEnum";
import { LoadSymbol } from "building-types/electrical/electricalTypesEnum";
import type { DocEntityBase } from "building-types/entities/docEntityBase";
import { FoundationComponentType } from "building-types/foundations/foundationComponentEntityTypes";
import type { FoundationCondition, FoundationSuperSystemType } from "building-types/foundations/foundationEnums";
import { FoundationSubSystemType } from "building-types/foundations/foundationEnums";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { CaseworkCatalogSettings } from "building-types/settings/caseworkCatalogSettings";
import type { ColumnCatalogSettings } from "building-types/settings/columnCatalogSettings";
import type { DoorCatalogSettings } from "building-types/settings/doorCatalogSettings";
import type { DrawingCatalogSettings } from "building-types/settings/drawingCatalogSettings";
import type { ElectricalItemCatalogSettings } from "building-types/settings/electricalItemCatalogSettings";
import type { FoundationHash } from "building-types/settings/foundationSettingsTypes";
import { settingsTypeToHomeSettingsField } from "building-types/settings/homeSettings";
import type { ProfileCatalogSettings } from "building-types/settings/profileCatalogSettings";
import type { RailingCatalogSettings } from "building-types/settings/railingCatalogSettings";
import { SettingsType } from "building-types/settings/settingsType";
import type { StairCatalogSettings } from "building-types/settings/stairCatalogSettings";
import type { WallCatalogSettings } from "building-types/settings/wallCatalogSettings";
import type { WindowCatalogSettings } from "building-types/settings/windowCatalogSettings";
import { WindowOperationType } from "building-types/settings/windowTypesEnum";
import type { DrawingRef } from "building-types/views/sheetsTypes";
import type { RWTable } from "common/data/table";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { isObject } from "common/utils/isObject";
import objectIsEmpty from "common/utils/objectIsEmpty";
import type { Mutable } from "common/utils/types";
import { docGetTableEntity } from "doc/entity/docGetTableEntity";
import { deriveHomeSettingsProvenance, type SettingsFragmentProvenance } from "doc/generate/deriveSettingsProvenance";
import { defaultDrawingCatalog } from "doc/plans/drawingCatalog";
import { isProtectedDrawingType } from "doc/plans/drawingTools";
import { isDoorPlacement, isWindowPlacement } from "doc/utils/openingTools";
import { isValidFoundationHash } from "./foundation/foundationSettingsTools";

export type CatalogSettings =
  | DrawingCatalogSettings
  | ProfileCatalogSettings
  | ColumnCatalogSettings
  | CaseworkCatalogSettings
  | DoorCatalogSettings
  | RailingCatalogSettings
  | WindowCatalogSettings
  | WallCatalogSettings
  | ElectricalItemCatalogSettings
  | StairCatalogSettings;

export type CatalogSettingsType = CatalogSettings["type"];
type CatalogSettingsByType<T extends CatalogSettingsType> = Extract<CatalogSettings, { type: T }>;

export const catalogLabels: { [key in CatalogSettingsType]: string } = {
  [SettingsType.WALL_CATALOG_SETTINGS]: "Wall Catalog",
  [SettingsType.DRAWING_CATALOG_SETTINGS]: "Drawing Catalog",
  [SettingsType.CASEWORK_CATALOG_SETTINGS]: "Casework Catalog",
  [SettingsType.DOOR_CATALOG_SETTINGS]: "Door Catalog",
  [SettingsType.WINDOW_CATALOG_SETTINGS]: "Window Catalog",
  [SettingsType.COLUMN_CATALOG_SETTINGS]: "Column Catalog",
  [SettingsType.PROFILE_CATALOG_SETTINGS]: "Profile Catalog",
  [SettingsType.STAIR_CATALOG_SETTINGS]: "Stair Catalog",
  [SettingsType.RAILING_CATALOG_SETTINGS]: "Railing Catalog",
  [SettingsType.ELECTRICAL_ITEM_CATALOG_SETTINGS]: "Electrical Item Catalog",
};

type MaterialSettingsType = SettingsType.INTERIOR_MATERIAL_SETTINGS | SettingsType.EXTERIOR_MATERIAL_SETTINGS;
export const materialLabels: { [key in MaterialSettingsType]: string } = {
  [SettingsType.INTERIOR_MATERIAL_SETTINGS]: "Interior Material Settings",
  [SettingsType.EXTERIOR_MATERIAL_SETTINGS]: "Exterior Material Settings",
};

export const docEntityTypeToRelatedCatalogType: { [key in DocEntityType]?: CatalogSettingsType[] } = {
  STAIR_SYSTEM: [SettingsType.STAIR_CATALOG_SETTINGS],
  STAIR_BLOCK: [SettingsType.STAIR_CATALOG_SETTINGS],

  RAILING: [SettingsType.RAILING_CATALOG_SETTINGS],
  RAILING_POST: [SettingsType.RAILING_CATALOG_SETTINGS],

  SHEET: [SettingsType.DRAWING_CATALOG_SETTINGS],
  DRAWING_REF: [SettingsType.DRAWING_CATALOG_SETTINGS],

  CASEWORK: [SettingsType.CASEWORK_CATALOG_SETTINGS],

  EDGE_BEHAVIOR: [
    SettingsType.WALL_CATALOG_SETTINGS, // WALL_ASSEMBLY_OVERRIDE and WALL_ASSEMBLY_LAYER_OVERRIDE
    SettingsType.COLUMN_CATALOG_SETTINGS, // PORCH_COLUMN_PATTERN
  ],

  COLUMN: [SettingsType.COLUMN_CATALOG_SETTINGS],

  OPENING: [SettingsType.WINDOW_CATALOG_SETTINGS, SettingsType.DOOR_CATALOG_SETTINGS],

  FLOOR: [],

  ELECTRICAL_LAYOUT: [SettingsType.ELECTRICAL_ITEM_CATALOG_SETTINGS],
  ELECTRICAL_DEVICE: [SettingsType.ELECTRICAL_ITEM_CATALOG_SETTINGS],
};

/**
 * This is purposely exhaustive so adding a new settings type will trigger a type error
 * and force the developer to identify the settings type as a catalog settings type
 */
export const settingsTypeIsCatalogSettingsTypeTable: { [type in SettingsType]: boolean } = {
  // catalogs
  [SettingsType.CASEWORK_CATALOG_SETTINGS]: true,
  [SettingsType.DOOR_CATALOG_SETTINGS]: true,
  [SettingsType.WALL_CATALOG_SETTINGS]: true,
  [SettingsType.STAIR_CATALOG_SETTINGS]: true,
  [SettingsType.COLUMN_CATALOG_SETTINGS]: true,
  [SettingsType.WINDOW_CATALOG_SETTINGS]: true,
  [SettingsType.DRAWING_CATALOG_SETTINGS]: true,
  [SettingsType.PROFILE_CATALOG_SETTINGS]: true,
  [SettingsType.RAILING_CATALOG_SETTINGS]: true,
  [SettingsType.ELECTRICAL_ITEM_CATALOG_SETTINGS]: true,
  [SettingsType.EXTERIOR_PROP_SETTINGS]: true,
  [SettingsType.INTERIOR_PROP_SETTINGS]: true,

  // not catalogs
  [SettingsType.WAINSCOTING_SETTINGS]: false,
  [SettingsType.WALL_FRAMING_SETTINGS]: false,
  [SettingsType.PORCH_SETTINGS]: false,
  [SettingsType.REAR_PORCH_SETTINGS]: false,
  [SettingsType.ROOF_SETTINGS]: false,
  [SettingsType.DORMER_SETTINGS]: false,
  [SettingsType.ROOF_FRAMING_SETTINGS]: false,
  [SettingsType.WINDOW_FRAMING_SETTINGS]: false,
  [SettingsType.DOOR_FRAMING_SETTINGS]: false,
  [SettingsType.WINDOW_WRAPPING_SETTINGS]: false,
  [SettingsType.DOOR_WRAPPING_SETTINGS]: false,
  [SettingsType.GARAGE_SETTINGS]: false,
  [SettingsType.DOOR_SETTINGS]: false,
  [SettingsType.WINDOW_SETTINGS]: false,
  [SettingsType.WINDOW_MATERIAL_SETTINGS]: false,
  [SettingsType.AWNING_SETTINGS]: false,
  [SettingsType.EXTERIOR_TRIM_SETTINGS]: false,
  [SettingsType.PLAN_SETTINGS]: false,
  [SettingsType.MASTER_PLAN_SETTINGS]: false,
  [SettingsType.SCHEDULE_SETTINGS]: false,
  [SettingsType.KEY_MEASURES_SETTINGS]: false,
  [SettingsType.FOUNDATION_SETTINGS]: false,
  [SettingsType.WALL_SETTINGS]: false,
  [SettingsType.INTERIOR_TRIM_SETTINGS]: false,
  [SettingsType.INTERIOR_MATERIAL_SETTINGS]: false,
  [SettingsType.STRUCTURE_SETTINGS]: false,
  [SettingsType.ELECTRICAL_SETTINGS]: false,
  [SettingsType.EXTERIOR_MATERIAL_SETTINGS]: false,
  [SettingsType.WINDOW_MULLION_SETTINGS]: false,
  [SettingsType.WINDOW_SHUTTER_SETTINGS]: false,
  [SettingsType.EXTERIOR_WINDOW_CASING_SETTINGS]: false,
  [SettingsType.EXTERIOR_DOOR_CASING_SETTINGS]: false,
  [SettingsType.INTERIOR_DOOR_CASING_SETTINGS]: false,
  [SettingsType.VIEW_NOTE_SETTINGS]: false,
  [SettingsType.CASEWORK_SETTINGS]: false,
  [SettingsType.FLOOR_SETTINGS]: false,
  [SettingsType.INTERIOR_WINDOW_CASING_SETTINGS]: false,
  [SettingsType.HEADER_FRAMING_SETTINGS]: false,
};

export const CATALOG_ALL_FILTER = "ALL" as const;

export const foundationFilters: { [key in "ASSEMBLIES" | "COMPONENTS"]: string[] } = {
  ASSEMBLIES: [CATALOG_ALL_FILTER, ...Object.values(FoundationSubSystemType)],
  COMPONENTS: [CATALOG_ALL_FILTER, ...Object.values(FoundationComponentType)],
};

export const catalogFilters: { [key in CatalogSettings["type"]]: string[] } = {
  DOOR_CATALOG_SETTINGS: [CATALOG_ALL_FILTER, ...Object.values(DoorOperationType)],
  CASEWORK_CATALOG_SETTINGS: [CATALOG_ALL_FILTER, ...Object.values(CabinetType), ...Object.values(ShelvingType)],
  WALL_CATALOG_SETTINGS: [CATALOG_ALL_FILTER],
  DRAWING_CATALOG_SETTINGS: [
    CATALOG_ALL_FILTER,
    ...Object.values(DrawingType).filter((t) => !isProtectedDrawingType[t]),
  ],
  STAIR_CATALOG_SETTINGS: [],
  WINDOW_CATALOG_SETTINGS: [CATALOG_ALL_FILTER, ...Object.values(WindowOperationType)],
  COLUMN_CATALOG_SETTINGS: [CATALOG_ALL_FILTER],
  PROFILE_CATALOG_SETTINGS: [CATALOG_ALL_FILTER],
  RAILING_CATALOG_SETTINGS: [CATALOG_ALL_FILTER],
  ELECTRICAL_ITEM_CATALOG_SETTINGS: [CATALOG_ALL_FILTER, ...Object.values(LoadSymbol)],
};

export function docEnforceCatalogIntegrity(originalDoc: Doc, catalogSetting: CatalogSettings): Doc {
  const doc = { ...originalDoc };
  switch (catalogSetting.type) {
    case SettingsType.DRAWING_CATALOG_SETTINGS:
      const drawingRefs: RWTable<DrawingRef> = {};
      let validDrawings: RWTable<Drawing> = {};

      /*
        Gather all drawings into a single object for easier reference later
      */
      for (const id in doc.settings) {
        const setting = doc.settings[id];

        if (setting.type !== SettingsType.DRAWING_CATALOG_SETTINGS) {
          continue;
        }

        validDrawings = { ...defaultDrawingCatalog, ...validDrawings, ...(setting as DrawingCatalogSettings).drawings };
      }

      /*
        check for orphaned drawingRefs, which will occur when a drawing in a drawingCatalog
        that is tied to a sheet or foundationCondition is removed
      */

      for (const id in doc.drawingRefs) {
        const drawingRef = doc.drawingRefs[id];

        if (!drawingRef.drawingId) {
          drawingRefs[id] = drawingRef;
          continue;
        }

        if (!validDrawings[drawingRef.drawingId]) {
          continue;
        }

        drawingRefs[drawingRef.id] = drawingRef;
      }

      /*
        ensure that any foundation conditions referencing removed drawing catalog items
        have their drawingId reset to NULL to allow placeholder static image to render
        in plan set
       */

      for (const id in doc.settings) {
        const setting = doc.settings[id];

        if (setting.type !== SettingsType.FOUNDATION_SETTINGS) {
          continue;
        }

        for (const key in setting) {
          if (isValidFoundationHash(key)) {
            const settingKey = key as FoundationHash<FoundationSuperSystemType, FoundationSubSystemType>;
            const foundationSetting = setting[settingKey];

            for (const key in foundationSetting?.foundationConditions) {
              const conditionKey = key as FoundationCondition;
              const foundationCondition = foundationSetting.foundationConditions[conditionKey];

              if (foundationCondition?.drawingId === undefined || !validDrawings[foundationCondition.drawingId]) {
                (foundationCondition as any).drawingId = DrawingId.NULL;
              }
            }
          }
        }
      }

      return {
        ...doc,
        drawingRefs,
      };

    // TODO: add additional integrity checks for other catalogs
    case SettingsType.CASEWORK_CATALOG_SETTINGS:
    case SettingsType.STAIR_CATALOG_SETTINGS:
    case SettingsType.COLUMN_CATALOG_SETTINGS:
    case SettingsType.DOOR_CATALOG_SETTINGS:
    case SettingsType.ELECTRICAL_ITEM_CATALOG_SETTINGS:
    case SettingsType.PROFILE_CATALOG_SETTINGS:
    case SettingsType.RAILING_CATALOG_SETTINGS:
    case SettingsType.WALL_CATALOG_SETTINGS:
    case SettingsType.WINDOW_CATALOG_SETTINGS:
      return doc;
  }
}

export const catalogsToOmitFromBidirectionalLinking: { [key in SettingsType]?: boolean } = {
  [SettingsType.PROFILE_CATALOG_SETTINGS]: true,
};

export function docGetCatalogInstanceEntities(doc: Doc, catalog: CatalogSettings): RWTable<DocEntityBase<any>> | null {
  if (!catalog) {
    return null;
  }

  switch (catalog.type) {
    case SettingsType.ELECTRICAL_ITEM_CATALOG_SETTINGS:
      {
        const instanceEntities: Mutable<Doc["settings"]> = {};

        for (const id in doc.settings) {
          const setting = doc.settings[id];

          if (setting.type !== SettingsType.ELECTRICAL_SETTINGS) {
            continue;
          }

          for (const ruleId in setting.rules) {
            const rule = setting.rules[ruleId];

            if (
              rule?.electricalLayout?.loadType === undefined ||
              !catalog.loadTypes?.[rule.electricalLayout?.loadType]
            ) {
              continue;
            }

            instanceEntities[id] = setting;
          }

          return instanceEntities;
        }
      }
      break;
    case SettingsType.DRAWING_CATALOG_SETTINGS: {
      if (objectIsEmpty(doc.drawingRefs)) {
        return null;
      }

      const instanceEntities: Mutable<Doc["drawingRefs"]> = {};

      for (const id in doc.drawingRefs) {
        const drawingRef = doc.drawingRefs[id];

        if (catalog.drawings?.[drawingRef.drawingId]) {
          instanceEntities[id] = drawingRef;
        }
      }

      return instanceEntities;
    }
    case SettingsType.PROFILE_CATALOG_SETTINGS:
      break;
    case SettingsType.COLUMN_CATALOG_SETTINGS: {
      if (objectIsEmpty(doc.columns)) {
        return null;
      }

      const instanceEntities: Mutable<Doc["columns"]> = {};

      for (const id in doc.columns) {
        const column = doc.columns[id];

        if (catalog.columnTable?.[column.catalogColumnId]) {
          instanceEntities[id] = column;
        }
      }

      return instanceEntities;
    }
    case SettingsType.DOOR_CATALOG_SETTINGS: {
      if (objectIsEmpty(doc.openings)) {
        return null;
      }

      const instanceEntities: Mutable<Doc["openings"]> = {};

      for (const id in doc.openings) {
        const opening = doc.openings[id];

        if (!isDoorPlacement(opening)) {
          continue;
        }

        if (catalog.doorTypes?.[opening.doorType]) {
          instanceEntities[id] = opening;
        }
      }

      return instanceEntities;
    }
    case SettingsType.RAILING_CATALOG_SETTINGS: {
      if (objectIsEmpty(doc.railings)) {
        return null;
      }

      const instanceEntities: Mutable<Doc["railings"]> = {};

      for (const id in doc.railings) {
        const railing = doc.railings[id];

        if (catalog.railingSettingsTable?.[railing.railingSettingId]) {
          instanceEntities[id] = railing;
        }
      }

      return instanceEntities;
    }
    case SettingsType.WINDOW_CATALOG_SETTINGS: {
      if (objectIsEmpty(doc.openings)) {
        return null;
      }

      const instanceEntities: Mutable<Doc["openings"]> = {};

      for (const id in doc.openings) {
        const opening = doc.openings[id];

        if (!isWindowPlacement(opening)) {
          continue;
        }

        if (catalog.windowTypes?.[opening.windowType]) {
          instanceEntities[id] = opening;
        }
      }

      return instanceEntities;
    }
    case SettingsType.WALL_CATALOG_SETTINGS:
      {
        const instanceEntities: Mutable<Doc["settings"]> = {};

        for (const id in doc.settings) {
          const setting = doc.settings[id];

          if (setting.type !== SettingsType.WALL_SETTINGS) {
            continue;
          }

          /**
           *  Note: Materials are currently not included here in lieu of an
           *  ongoing refactoring effort to move wall materials to a separate catalog
           */

          for (const wallConditionId in setting.wallConditions) {
            const wallCondition = setting.wallConditions[wallConditionId];

            if (!catalog.wallTypes?.[wallCondition.wallTypeId]) {
              continue;
            }

            instanceEntities[id] = setting;
          }

          return instanceEntities;
        }
      }
      break;
    case SettingsType.STAIR_CATALOG_SETTINGS: {
      if (objectIsEmpty(doc.stairSystems)) {
        return null;
      }

      const instanceEntities: Mutable<Doc["stairSystems"]> = {};

      for (const id in doc.stairSystems) {
        const stairSystem = doc.stairSystems[id];

        if (stairSystem.stairSettingId && catalog.stairSettingsTable?.[stairSystem.stairSettingId]) {
          instanceEntities[id] = stairSystem;
        }
      }

      return instanceEntities;
    }
    case SettingsType.CASEWORK_CATALOG_SETTINGS:
      if (objectIsEmpty(doc.casework)) {
        return null;
      }

      const instanceEntities: Mutable<Doc["casework"]> = {};

      for (const id in doc.casework) {
        const casework = doc.casework[id];

        if (
          casework.type === "FIXED" &&
          !!casework?.catalogDescriptorId &&
          catalog.caseworkTypes?.[casework.catalogDescriptorId]
        ) {
          instanceEntities[id] = casework;
        }
      }

      return instanceEntities;

    default:
      assertUnreachable(catalog);
  }

  return null;
}

export function docGetCatalogSetting(doc: Doc, handle: DocEntityHandle) {
  if (!handle) {
    return null;
  }

  const getSettingsProvenance = (() => {
    let settingsProvenance: SettingsFragmentProvenance | undefined;

    return () => {
      if (settingsProvenance === undefined) {
        settingsProvenance = deriveHomeSettingsProvenance({
          settings: doc.settings ?? {},
          settingsSets: doc.settingsSets ?? {},
          optionChoices: doc.optionChoices ?? {},
          optionSets: doc.optionSets ?? {},
        });
      }

      return settingsProvenance;
    };
  })();

  const isCatalogSettingOfType = <T extends CatalogSettingsType>(
    setting: unknown,
    expectedType: T,
  ): setting is CatalogSettingsByType<T> => {
    return typeof setting === "object" && setting !== null && (setting as { type?: unknown }).type === expectedType;
  };

  const getCatalogSettingById = <T extends CatalogSettingsType>(
    id: string | undefined,
    expectedType: T,
  ): (CatalogSettingsByType<T> & { id: string }) | undefined => {
    if (id === undefined) {
      return undefined;
    }

    const setting = doc.settings[id];
    if (!isCatalogSettingOfType(setting, expectedType)) {
      return undefined;
    }

    return { ...setting, id };
  };

  const getCatalogSettingIdFromProvenance = (
    settingsType: SettingsType,
    fieldName: string,
    fieldKey: string | undefined,
  ): string | undefined => {
    const homeSettingsField = settingsTypeToHomeSettingsField[settingsType];
    if (homeSettingsField === undefined) {
      return undefined;
    }

    const fieldProvenance = getSettingsProvenance()?.[homeSettingsField];
    if (!isObject(fieldProvenance)) {
      return undefined;
    }

    const tableProvenance = fieldProvenance[fieldName];
    if (typeof tableProvenance === "string") {
      return tableProvenance;
    }

    if (fieldKey === undefined || !isObject(tableProvenance)) {
      return undefined;
    }

    const settingId = tableProvenance[fieldKey];
    return typeof settingId === "string" ? settingId : undefined;
  };

  const getCatalogSettingFromProvenance = <T extends CatalogSettingsType>(
    settingsType: T,
    fieldName: string,
    fieldKey: string | undefined,
  ): (CatalogSettingsByType<T> & { id: string }) | undefined => {
    const settingId = getCatalogSettingIdFromProvenance(settingsType, fieldName, fieldKey);
    return getCatalogSettingById(settingId, settingsType);
  };

  const getProfileCatalogSetting = (profileId: string | undefined): ProfileCatalogSettings | undefined => {
    if (profileId) {
      const activeCatalogSetting = getCatalogSettingFromProvenance(
        SettingsType.PROFILE_CATALOG_SETTINGS,
        "profileTable",
        profileId,
      );
      if (activeCatalogSetting?.profileTable?.[profileId]) {
        return activeCatalogSetting;
      }
    }

    // Returns the first profile catalog settings. Assumes at most one per doc.
    for (const id in doc.settings) {
      const setting = doc.settings[id];

      if (setting.type !== SettingsType.PROFILE_CATALOG_SETTINGS) {
        continue;
      }

      return { ...setting, id } as ProfileCatalogSettings;
    }

    return undefined;
  };

  switch (handle.type) {
    case "SETTINGS": {
      const setting = docGetTableEntity(doc, handle);
      if (!setting) {
        return null;
      }

      switch (setting.type) {
        case SettingsType.ELECTRICAL_SETTINGS: {
          for (const ruleId in setting.rules) {
            const rule = setting.rules[ruleId];
            const loadType = rule?.electricalLayout?.loadType;
            if (loadType === undefined) {
              continue;
            }

            const activeCatalogSetting = getCatalogSettingFromProvenance(
              SettingsType.ELECTRICAL_ITEM_CATALOG_SETTINGS,
              "loadTypes",
              loadType,
            );
            if (activeCatalogSetting?.loadTypes?.[loadType]) {
              return activeCatalogSetting;
            }
          }

          for (const id in doc.settings) {
            const localSetting = doc.settings[id];

            if (localSetting.type !== SettingsType.ELECTRICAL_ITEM_CATALOG_SETTINGS) {
              continue;
            }

            if (objectIsEmpty(localSetting.loadTypes)) {
              continue;
            }

            for (const ruleId in setting.rules) {
              const rule = setting.rules[ruleId];

              if (
                rule?.electricalLayout?.loadType === undefined ||
                !localSetting.loadTypes?.[rule.electricalLayout.loadType]
              ) {
                continue;
              }

              return localSetting;
            }
          }
          break;
        }
        case SettingsType.WALL_SETTINGS: {
          for (const wallConditionId in setting.wallConditions) {
            const wallCondition = setting.wallConditions[wallConditionId];
            const wallTypeId = wallCondition.wallTypeId;
            if (wallTypeId === undefined) {
              continue;
            }

            const activeCatalogSetting = getCatalogSettingFromProvenance(
              SettingsType.WALL_CATALOG_SETTINGS,
              "wallTypes",
              wallTypeId,
            );
            if (activeCatalogSetting?.wallTypes?.[wallTypeId]) {
              return activeCatalogSetting;
            }
          }

          for (const id in doc.settings) {
            const localSetting = doc.settings[id];

            if (localSetting.type !== SettingsType.WALL_CATALOG_SETTINGS) {
              continue;
            }

            for (const wallConditionId in setting.wallConditions) {
              const wallCondition = setting.wallConditions[wallConditionId];

              if (!localSetting.wallTypes?.[wallCondition.wallTypeId]) {
                continue;
              }

              return localSetting;
            }
          }
          break;
        }
        default:
          break;
      }

      break;
    }

    case "FOUNDATION": {
      break;
    }
    case "COLUMN": {
      const column = docGetTableEntity(doc, handle);

      if (!column) {
        return null;
      }

      const activeCatalogSetting = getCatalogSettingFromProvenance(
        SettingsType.COLUMN_CATALOG_SETTINGS,
        "columnTable",
        column.catalogColumnId,
      );
      if (activeCatalogSetting?.columnTable?.[column.catalogColumnId]) {
        return activeCatalogSetting;
      }

      for (const id in doc.settings) {
        const setting = doc.settings[id];

        if (setting.type !== SettingsType.COLUMN_CATALOG_SETTINGS) {
          continue;
        }

        if (objectIsEmpty(setting.columnTable)) {
          continue;
        }

        if (!setting.columnTable?.[column.catalogColumnId]) {
          continue;
        }

        const typedSetting = getCatalogSettingById(id, SettingsType.COLUMN_CATALOG_SETTINGS);
        if (typedSetting) {
          return typedSetting;
        }
      }

      break;
    }
    case "CASEWORK": {
      const casework = docGetTableEntity(doc, handle);

      if (!casework) {
        return null;
      }

      if (casework.type === "FIXED" && casework.catalogDescriptorId !== undefined) {
        const activeCatalogSetting = getCatalogSettingFromProvenance(
          SettingsType.CASEWORK_CATALOG_SETTINGS,
          "caseworkTypes",
          casework.catalogDescriptorId,
        );
        if (activeCatalogSetting?.caseworkTypes?.[casework.catalogDescriptorId]) {
          return activeCatalogSetting;
        }
      }

      for (const id in doc.settings) {
        const setting = doc.settings[id];

        if (setting.type !== SettingsType.CASEWORK_CATALOG_SETTINGS) {
          continue;
        }

        if (objectIsEmpty(setting.caseworkTypes)) {
          continue;
        }

        if (
          casework.type === "FIXED" &&
          (casework?.catalogDescriptorId === undefined || !setting.caseworkTypes?.[casework.catalogDescriptorId])
        ) {
          continue;
        }

        return setting;
      }

      break;
    }
    case "STAIR_SYSTEM": {
      const stairSystem = docGetTableEntity(doc, handle);

      if (!stairSystem) {
        return null;
      }

      if (stairSystem.stairSettingId) {
        const activeCatalogSetting = getCatalogSettingFromProvenance(
          SettingsType.STAIR_CATALOG_SETTINGS,
          "stairSettingsTable",
          stairSystem.stairSettingId,
        );
        if (activeCatalogSetting?.stairSettingsTable?.[stairSystem.stairSettingId]) {
          return activeCatalogSetting;
        }
      }

      let fallback: StairCatalogSettings | null = null;
      for (const id in doc.settings) {
        const setting = doc.settings[id];

        if (setting.type !== SettingsType.STAIR_CATALOG_SETTINGS) {
          continue;
        }

        const settingWithId = { ...setting, id } as StairCatalogSettings;
        if (objectIsEmpty(setting.stairSettingsTable)) {
          if (fallback === null) {
            fallback = settingWithId;
          }
          continue;
        }

        if (!stairSystem.stairSettingId || !setting.stairSettingsTable?.[stairSystem.stairSettingId]) {
          if (fallback === null) {
            fallback = settingWithId;
          }
          continue;
        }

        return settingWithId;
      }

      return fallback;
    }
    case "RAILING": {
      const railing = docGetTableEntity(doc, handle);

      if (!railing) {
        return null;
      }

      if (railing.railingSettingId) {
        const activeCatalogSetting = getCatalogSettingFromProvenance(
          SettingsType.RAILING_CATALOG_SETTINGS,
          "railingSettingsTable",
          railing.railingSettingId,
        );
        if (activeCatalogSetting?.railingSettingsTable?.[railing.railingSettingId]) {
          return activeCatalogSetting;
        }
      }

      let fallback: RailingCatalogSettings | null = null;
      for (const id in doc.settings) {
        const setting = doc.settings[id];

        if (setting.type !== SettingsType.RAILING_CATALOG_SETTINGS) {
          continue;
        }

        const settingWithId = { ...setting, id } as RailingCatalogSettings;
        if (objectIsEmpty(setting.railingSettingsTable)) {
          if (fallback === null) {
            fallback = settingWithId;
          }
          continue;
        }

        if (!railing.railingSettingId || !setting.railingSettingsTable?.[railing.railingSettingId]) {
          if (fallback === null) {
            fallback = settingWithId;
          }
          continue;
        }

        return settingWithId;
      }

      return fallback;
    }
    case "OPENING": {
      const opening = docGetTableEntity(doc, handle);

      if (!opening) {
        return null;
      }

      switch (opening.type) {
        case "WINDOW":
        case "WINDOW_V2": {
          const activeCatalogSetting = getCatalogSettingFromProvenance(
            SettingsType.WINDOW_CATALOG_SETTINGS,
            "windowTypes",
            opening.windowType,
          );
          if (activeCatalogSetting?.windowTypes?.[opening.windowType]) {
            return activeCatalogSetting;
          }

          for (const id in doc.settings) {
            const setting = doc.settings[id];
            if (setting.type !== SettingsType.WINDOW_CATALOG_SETTINGS) {
              continue;
            }

            if (objectIsEmpty(setting.windowTypes)) {
              continue;
            }

            if (!setting.windowTypes?.[opening.windowType]) {
              continue;
            }

            return { ...setting, id } as WindowCatalogSettings & { id: string };
          }
          break;
        }
        case "DOOR":
        case "DOOR_V2": {
          const activeCatalogSetting = getCatalogSettingFromProvenance(
            SettingsType.DOOR_CATALOG_SETTINGS,
            "doorTypes",
            opening.doorType,
          );
          if (activeCatalogSetting?.doorTypes?.[opening.doorType]) {
            return activeCatalogSetting;
          }

          for (const id in doc.settings) {
            const setting = doc.settings[id];
            if (setting.type !== SettingsType.DOOR_CATALOG_SETTINGS) {
              continue;
            }

            if (objectIsEmpty(setting.doorTypes)) {
              continue;
            }

            if (!setting.doorTypes?.[opening.doorType]) {
              continue;
            }

            return { ...setting, id } as DoorCatalogSettings & { id: string };
          }
          break;
        }
        case "SOLID":
          break;
      }

      break;
    }
    case "ROOF_OPENING": {
      const roofOpening = docGetTableEntity(doc, handle);

      if (!roofOpening) {
        return null;
      }

      const activeCatalogSetting = getCatalogSettingFromProvenance(
        SettingsType.WINDOW_CATALOG_SETTINGS,
        "windowTypes",
        roofOpening.windowType,
      );
      if (activeCatalogSetting?.windowTypes?.[roofOpening.windowType]) {
        return activeCatalogSetting;
      }

      for (const id in doc.settings) {
        const setting = doc.settings[id];

        if (setting.type !== SettingsType.WINDOW_CATALOG_SETTINGS) {
          continue;
        }

        if (objectIsEmpty(setting.windowTypes)) {
          continue;
        }

        if (!setting.windowTypes?.[roofOpening.windowType]) {
          continue;
        }

        return { ...setting, id } as WindowCatalogSettings & { id: string };
      }

      break;
    }
    case "DRAWING_REF": {
      const drawingRef = docGetTableEntity(doc, handle);

      if (!drawingRef) {
        return null;
      }

      const activeCatalogSetting = getCatalogSettingFromProvenance(
        SettingsType.DRAWING_CATALOG_SETTINGS,
        "drawings",
        drawingRef.drawingId,
      );
      if (activeCatalogSetting?.drawings?.[drawingRef.drawingId]) {
        return activeCatalogSetting;
      }

      for (const id in doc.settings) {
        const setting = doc.settings[id];

        if (setting.type !== SettingsType.DRAWING_CATALOG_SETTINGS) {
          continue;
        }

        if (objectIsEmpty(setting.drawings)) {
          continue;
        }

        if (!setting.drawings?.[drawingRef.drawingId]) {
          continue;
        }

        return setting as DrawingCatalogSettings;
      }

      break;
    }
    case "ELECTRICAL_LAYOUT": {
      const electricalLayout = docGetTableEntity(doc, handle);
      const loadType =
        electricalLayout && "loadType" in electricalLayout && typeof electricalLayout.loadType === "string"
          ? electricalLayout.loadType
          : undefined;
      if (loadType !== undefined) {
        const activeCatalogSetting = getCatalogSettingFromProvenance(
          SettingsType.ELECTRICAL_ITEM_CATALOG_SETTINGS,
          "loadTypes",
          loadType,
        );
        if (activeCatalogSetting?.loadTypes?.[loadType]) {
          return activeCatalogSetting;
        }
      }

      // Returns the first electrical item catalog settings. Assumes at most one per doc.
      for (const id in doc.settings) {
        const setting = doc.settings[id];

        if (setting.type !== SettingsType.ELECTRICAL_ITEM_CATALOG_SETTINGS) {
          continue;
        }

        return { ...setting, id } as ElectricalItemCatalogSettings;
      }

      break;
    }
    case "FACADE_SWEEP_STRATUM": {
      const facadeSweepStratum = docGetTableEntity(doc, handle);
      return getProfileCatalogSetting(facadeSweepStratum?.sweep?.profileId);
    }
    case "WALL_PLANE_TRIM": {
      const wallPlaneTrim = docGetTableEntity(doc, handle);
      return getProfileCatalogSetting(wallPlaneTrim?.profileId);
    }
    default:
      return null;
  }

  return undefined;
}

export const MISSING_CATALOG_ITEM_NAME = "(Missing Catalog Item)";
export const WINDOW_REVIEW_NOTE = "REVIEW NOTE : USE WINDOW TYPE";

export function isCatalogSettings(setting: any): setting is CatalogSettings {
  return setting?.type && settingsTypeIsCatalogSettingsTypeTable[setting.type];
}

export function isDrawingCatalogSetting(entity: any): entity is DrawingCatalogSettings {
  return entity?.type === SettingsType.DRAWING_CATALOG_SETTINGS;
}
